// SCR-STAFF-920 — کارکنان (US-IAM-011، 013، 014): جدول نام، موبایل (پنهان‌سازی میانی)، نقش‌ها، شعب، وضعیت و ارائه‌دهنده بودن؛
// فیلتر وضعیت، نقش، شعبه و جستجو؛ دیالوگ دعوت؛ اقدام‌های ارسال مجدد دعوت، تعلیق، فعال‌سازی و لغو با ReasonDialog.
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type InviteMemberResult,
  type MembershipStatus,
  type MembershipView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, JalaliDate, ListPage, StatusChip, UpgradeBadge, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useDetailLinkClick, useDetailNavigate } from '../../layout/detailNavigation';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { WriteNotice } from '../settings/common';
import {
  iamKeys,
  maskMobile,
  MEMBERSHIP_STATUSES,
  membershipTone,
  useBranchOptions,
  useLimitLabel,
  useLimitUsage,
  useRoleLabel,
  useRoles,
} from './common';
import { InviteDialog } from './InviteDialog';
import { MemberActions } from './MemberActions';

export const SCR_STAFF_920 = 'SCR-STAFF-920';
export const STAFF_PATH = '/settings/staff';

const PAGE_SIZE = 50;

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function StaffListPage() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const openDetail = useDetailNavigate();
  const detailClick = useDetailLinkClick();
  const invite = useWriteAccess('iam.member.invite');
  const roleLabel = useRoleLabel();
  const roles = useRoles();
  const { branches } = useBranchOptions();
  const staffLimit = useLimitUsage('limit.staff_users');
  const limitLabel = useLimitLabel();
  const [status, setStatus] = useState<MembershipStatus | ''>('');
  const [role, setRole] = useState('');
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<InviteMemberResult | null>(null);
  const q = useDebounced(search.trim());

  const filters = useMemo(
    () => ({
      status: status || undefined,
      role: role || undefined,
      branchId: branchId || undefined,
      q: q || undefined,
    }),
    [status, role, branchId, q],
  );

  const members = useInfiniteQuery({
    queryKey: iamKeys.memberships(session?.tenantId, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/identity/memberships', {
          params: { query: { ...filters, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = members.data?.pages.flatMap((page) => page.items);
  const branchName = useMemo(() => new Map(branches.map((branch) => [branch.id, branch.name])), [branches]);

  const columns: DataTableColumn<MembershipView>[] = [
    {
      id: 'name',
      headerKey: 'iam:staff.columns.name',
      cell: (row) => (
        <Link
          component={RouterLink}
          to={`${STAFF_PATH}/${row.membershipId}`}
          onClick={detailClick(`${STAFF_PATH}/${row.membershipId}`)}
          data-testid={`member-link-${row.membershipId}`}
        >
          {row.displayName}
        </Link>
      ),
    },
    {
      id: 'mobile',
      headerKey: 'iam:staff.columns.mobile',
      cell: (row) => <bdi dir="ltr">{maskMobile(row.mobile)}</bdi>,
    },
    {
      id: 'roles',
      headerKey: 'iam:staff.columns.roles',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {row.roles.map((code) => (
            <Chip key={code} size="small" variant="outlined" label={roleLabel(code)} />
          ))}
        </Stack>
      ),
    },
    {
      id: 'branches',
      headerKey: 'iam:staff.columns.branches',
      cell: (row) =>
        row.branchIds
          .map((id) => branchName.get(id) || t('shell:branch.unnamed'))
          .join(t('iam:staff.list_separator')) || t('common:states.none'),
    },
    {
      id: 'status',
      headerKey: 'iam:staff.columns.status',
      cell: (row) => (
        <span data-testid={`member-status-${row.membershipId}`} data-status={row.status}>
          <StatusChip tone={membershipTone(row.status)} labelKey={`iam:status.${row.status}`} />
        </span>
      ),
    },
    {
      id: 'provider',
      headerKey: 'iam:staff.columns.provider',
      cell: (row) =>
        row.providerId ? (
          <StatusChip
            tone={row.providerStatus === 'Active' ? 'active' : 'inactive'}
            labelKey={`iam:provider.status.${row.providerStatus ?? 'Inactive'}`}
          />
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'invited',
      headerKey: 'iam:staff.columns.status_changed',
      cell: (row) =>
        row.statusChangedAt || row.invitedAt ? (
          <JalaliDate value={(row.statusChangedAt ?? row.invitedAt) as string} />
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'actions',
      headerKey: 'iam:staff.columns.actions',
      cell: (row) => <MemberActions member={row} />,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_920}
      titleKey="iam:staff.title"
      subtitleKey="iam:staff.subtitle"
      badges={
        staffLimit.known ? (
          <Chip size="small" variant="outlined" data-testid="staff-usage" label={limitLabel(staffLimit)} />
        ) : undefined
      }
      actions={
        <>
          {staffLimit.full && <UpgradeBadge feature="limit.staff_users" to={SUBSCRIPTION_PATH} />}
          {invite.permitted && (
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1OutlinedIcon />}
              onClick={() => setInviting(true)}
              disabled={!invite.canWrite}
              data-testid="member-invite"
            >
              {t('iam:staff.invite')}
            </Button>
          )}
        </>
      }
      filters={
        <Stack spacing={1}>
          {invite.permitted && <WriteNotice write={invite} />}
          {invited && (
            <Alert severity="success" onClose={() => setInvited(null)} data-testid="invite-success">
              {t('iam:invite.success')}
            </Alert>
          )}
        </Stack>
      }
    >
      <DataTable
        captionKey="iam:staff.caption"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.membershipId}
        isLoading={members.isPending}
        error={members.error}
        onRetry={() => void members.refetch()}
        hasMore={members.hasNextPage}
        onLoadMore={() => void members.fetchNextPage()}
        isFetchingMore={members.isFetchingNextPage}
        onRowClick={(row) => openDetail(`${STAFF_PATH}/${row.membershipId}`)}
        emptyTitleKey="iam:staff.empty"
        toolbar={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label={t('iam:staff.filters.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'member-search' } }}
            />
            <TextField
              select
              label={t('iam:staff.filters.status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as MembershipStatus | '')}
              sx={{ minWidth: 160 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">{t('iam:staff.filters.all')}</MenuItem>
              {MEMBERSHIP_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`iam:status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('iam:staff.filters.role')}
              value={role}
              onChange={(event) => setRole(event.target.value)}
              sx={{ minWidth: 180 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">{t('iam:staff.filters.all')}</MenuItem>
              {(roles.data?.items ?? []).map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {roleLabel(item.code)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('iam:staff.filters.branch')}
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              sx={{ minWidth: 180 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">{t('iam:staff.filters.all')}</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name || t('shell:branch.unnamed')}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />
      {inviting && (
        <InviteDialog
          open
          onClose={() => setInviting(false)}
          onInvited={(result) => {
            setInviting(false);
            setInvited(result);
          }}
        />
      )}
    </ListPage>
  );
}
